import { useCallback, useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { Clock, Loader2, ReceiptText, RefreshCw, UtensilsCrossed } from 'lucide-react'
import LanguageSwitcher from '../components/LanguageSwitcher'
import { useI18n } from '../i18n/useI18n'
import { api } from '../services/api'
import type { BinderBillState } from '../services/api/stores'
import { hhmm } from '../lib/timeCharge'
import { snapshotText } from '../lib/menuVariation'

/**
 * #2039 お客様が自分の伝票（バインダー）の金額を確かめるページ (`/stores/:id/binder?t=…`)。
 *
 * バインダーに貼った QR から開く。アプリも会員登録も要らない。時間で課金する店では「今いくらか」が
 * 分からないのが一番の不安なので、**今の合計**と**次に上がる時刻**を出す。
 *
 * - 認可は QR のトークンだけ (番号では読めない — 番号は 1 から順で、当てずっぽうで他人の伝票が
 *   見えてしまう)。トークンは URL に残す: 置いておいた画面を更新し続けるのに要る
 * - 金額の正はサーバ (レジの伝票と同じ部品で組み立てている)。ここでは足し算もしない
 * - 「残り ◯ 分」ではなく**時刻**を出す (#2036 と同じ理由: 置いておくと古くなる)
 * - 開いていない伝票は、前の客の内容を何も出さず「スタッフにお声がけください」
 */

const POLL_MS = 30_000

const yen = (n: number) => `¥${n.toLocaleString()}`

export default function BinderBill() {
  const { id: storeId } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('t') || ''
  const { t, locale } = useI18n()
  const [bill, setBill] = useState<BinderBillState | null>(null)
  const [problem, setProblem] = useState<'invalid' | 'failed' | null>(null)
  const [loadedAt, setLoadedAt] = useState<Date | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  const load = useCallback(async () => {
    if (!storeId || !token) {
      setProblem('invalid')
      return
    }
    setRefreshing(true)
    try {
      setBill(await api.getBinderBill(storeId, token))
      setLoadedAt(new Date())
      setProblem(null)
    } catch (e) {
      const status = (e as { status?: number })?.status
      // 404 = トークンが違う / 400 = バインダー形式の店ではない。どちらも客にできることは無い
      if (status === 404 || status === 400) setProblem('invalid')
      else {
        console.error('[BinderBill] load failed', e)
        // 一度でも読めていれば、古い金額を出したまま失敗だけ知らせる (真っ白にしない)
        setProblem('failed')
      }
    } finally {
      setRefreshing(false)
    }
  }, [storeId, token])

  // 置きっぱなしの画面が店の Firestore の枠を食わないようにする: 見えていない間は読まず、
  // 見えるようになった時に 1 回読む。トークン違い (直らない失敗) の後は読むのをやめる。
  const dead = problem === 'invalid'
  useEffect(() => {
    if (dead) return
    load()
    const tick = () => { if (!document.hidden) load() }
    const onVisible = () => { if (!document.hidden) load() }
    const id = window.setInterval(tick, POLL_MS)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [load, dead])

  // 注文に進めるのは、店が受け付けていて、会計が始まっておらず、支払いも済んでいないとき
  const canOrder = !!bill?.open && bill.customerOrder && !bill.checkoutRequested && !bill.paid
  // **トークンは、注文に進むときだけ渡す。** メニューは誰でも見られる共有できるページで、
  // そこにトークンを残すと、URL を送っただけで伝票が見えてしまう。
  const menuLink = !storeId ? '/'
    : canOrder ? `/stores/${storeId}?t=${encodeURIComponent(token)}` : `/stores/${storeId}`

  return (
    <div className="min-h-screen bg-warm-white safe-top safe-bottom">
      <div className="mobile-container mx-auto px-5 pt-5 pb-10">
        {/* 言語の切り替えは 10 個並ぶので、店名と同じ行に置くと店名が切れる */}
        <p className="font-body text-xs text-text-tertiary">{t('binderBill.title')}</p>
        <h1 className="font-jp font-bold text-lg text-text-primary">
          {(locale !== 'ja' && bill?.storeNameEn) || bill?.storeName || ' '}
        </h1>
        <LanguageSwitcher className="mt-2" />

        {problem === 'invalid' && (
          <Notice title={t('binderBill.invalidTitle')} body={t('binderBill.askStaff')} />
        )}

        {!bill && !problem && (
          <div className="mt-16 flex justify-center text-text-tertiary">
            <Loader2 className="animate-spin" size={28} />
          </div>
        )}

        {bill && problem !== 'invalid' && !bill.open && (
          <Notice
            badge={t('binderBill.binderNo', { n: bill.binderNumber })}
            title={t('binderBill.notOpenTitle')}
            body={t('binderBill.askStaff')}
          />
        )}

        {bill && problem !== 'invalid' && bill.open && bill.paid && (
          <Notice
            badge={t('binderBill.binderNo', { n: bill.binderNumber })}
            title={t('binderBill.paidTitle')}
            body={t('binderBill.paidBody')}
          />
        )}

        {bill && problem !== 'invalid' && bill.open && !bill.paid && (
          <>
            <div className="mt-4 rounded-2xl bg-navy text-white p-5 shadow-card">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 font-display text-sm font-bold">
                  <ReceiptText size={14} />
                  {t('binderBill.binderNo', { n: bill.binderNumber })}
                </span>
                {bill.partySize ? (
                  <span className="font-body text-xs opacity-80">
                    {t('binderBill.party', { n: bill.partySize })}
                  </span>
                ) : null}
              </div>
              <p className="mt-4 font-body text-xs opacity-80">{t('binderBill.total')}</p>
              <p className="font-display text-4xl font-bold tracking-tight" data-testid="binder-bill-total">
                {yen(bill.totalAmount ?? 0)}
              </p>
              <p className="mt-1 font-body text-[11px] opacity-70">{t('binderBill.taxIncluded')}</p>
            </div>

            <TimeChargeNote bill={bill} />

            {bill.checkoutRequested && (
              <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 font-body text-xs text-amber-800 leading-relaxed">
                {t('binderBill.checkoutRequested')}
              </p>
            )}

            <div className="mt-4 rounded-2xl bg-white shadow-card p-5">
              <p className="font-body text-sm font-semibold text-text-primary">
                {t('binderBill.itemsTitle')}
              </p>
              <BillLines bill={bill} locale={locale} />
            </div>

            <p className="mt-4 font-body text-xs text-text-tertiary leading-relaxed text-center">
              {t('binderBill.payAtRegister')}
            </p>

            {/* サーバも同じ規則。会計開始後の注文は 409 で断られるので「注文する」と誘わない */}
            <Link
              to={menuLink}
              className="mt-4 flex items-center justify-center gap-2 w-full h-12 rounded-xl border border-navy text-navy font-display font-semibold text-sm tap-effect hover:bg-navy/5"
            >
              <UtensilsCrossed size={16} />
              {t(canOrder ? 'binderBill.orderFromMenu' : 'binderBill.viewMenu')}
            </Link>
          </>
        )}

        {bill && problem !== 'invalid' && (
          <div className="mt-5 flex items-center justify-center gap-3 font-body text-[11px] text-text-tertiary">
            {problem === 'failed' ? (
              <span className="text-yokoso-red">{t('binderBill.loadFailed')}</span>
            ) : loadedAt ? (
              <span>{t('binderBill.updatedAt', { time: hhmm(loadedAt.toISOString()) })}</span>
            ) : null}
            <button
              type="button"
              onClick={load}
              disabled={refreshing}
              className="inline-flex items-center gap-1 text-navy underline tap-effect disabled:opacity-40"
            >
              <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
              {t('binderBill.refresh')}
            </button>
          </div>
        )}

        {!bill && problem === 'failed' && (
          <Notice title={t('binderBill.loadFailed')} body={t('binderBill.askStaff')} />
        )}
      </div>
    </div>
  )
}

function Notice({ badge, title, body }: { badge?: string; title: string; body: string }) {
  return (
    <div className="mt-10 rounded-2xl bg-white shadow-card p-6 text-center">
      {badge && (
        <span className="inline-block rounded-full bg-navy/10 text-navy px-3 py-1 font-display text-sm font-bold">
          {badge}
        </span>
      )}
      <p className="mt-3 font-jp font-bold text-base text-text-primary">{title}</p>
      <p className="mt-2 font-body text-sm text-text-secondary leading-relaxed">{body}</p>
    </div>
  )
}

/** 時間料金の今の状態。時間制でない伝票では何も出さない。 */
function TimeChargeNote({ bill }: { bill: BinderBillState }) {
  const { t } = useI18n()
  const tc = bill.timeCharge
  if (!tc) return null
  const text = tc.chargedPartySize <= 0
    ? t('binderBill.timeFree')
    : !tc.running
      ? t('binderBill.timeStopped')
      : tc.nextUnitAt
        ? t('binderBill.timeNext', { time: hhmm(tc.nextUnitAt), minutes: tc.unitMinutes })
        : t('binderBill.timeCapped')
  return (
    <div className="mt-3 flex items-start gap-2 rounded-xl border border-gray-100 bg-white px-4 py-3 font-body text-xs text-text-secondary leading-relaxed">
      <Clock size={14} className="mt-0.5 shrink-0 text-navy" />
      <span data-testid="binder-bill-time">{text}</span>
    </div>
  )
}

function BillLines({ bill, locale }: { bill: BinderBillState; locale: string }) {
  const { t } = useI18n()
  const items = (bill.orders ?? []).flatMap((o) => o.items)
  const extras = bill.extras ?? []
  if (items.length === 0 && extras.length === 0) {
    return <p className="mt-3 font-body text-xs text-text-tertiary">{t('binderBill.noItems')}</p>
  }
  const unit = bill.timeCharge?.unitMinutes ?? 0
  return (
    <ul className="mt-3 divide-y divide-gray-100">
      {items.map((it, i) => {
        // 時間料金の行は、伝票上の名前 (日本語のプラン名) ではなく、この画面の言葉で出す
        const isTime = it.timeCharge && it.unitPriceYen != null && it.unitCount != null
        // 伝票は 1 冊を数人で見る。訳は**見ている人の言語と同じときだけ**使う (中国語で頼んだ品を
        // 英語の客に中国語で出さない)。店員が入力した注文には訳が無いので、日本語以外で見ている
        // ときは英語名を主にする。
        const primary = isTime
          ? t('binderBill.timeLine', { minutes: unit, count: it.unitCount ?? 0 })
          : (it.locale === locale && it.nameLocalized) || (locale !== 'ja' && it.nameEn) || it.name
        const secondary = isTime
          ? t('binderBill.timeLineDetail', { price: yen(it.unitPriceYen ?? 0), count: it.unitCount ?? 0 })
          : primary !== it.name ? it.name : ''
        const variations = snapshotText(it.variations)
        return (
          <li key={i} className="py-2.5 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-body text-sm text-text-primary">
                {primary}
                {/* 時間料金の数量は人数。「30分 × 1 ×2」と並ぶと何の 2 か分からない */}
                <span className="ml-1.5 text-text-tertiary">
                  {isTime ? t('binderBill.party', { n: it.quantity }) : `×${it.quantity}`}
                </span>
              </p>
              {(secondary || variations) && (
                <p className="font-body text-[11px] text-text-tertiary mt-0.5">
                  {[secondary, variations].filter(Boolean).join(' ・ ')}
                </p>
              )}
            </div>
            <span className="font-display text-sm font-semibold text-text-primary shrink-0">
              {yen(it.price * it.quantity)}
            </span>
          </li>
        )
      })}
      {extras.map((x, i) => (
        <li key={`x${i}`} className="py-2.5 flex items-start justify-between gap-3">
          <p className="font-body text-sm text-text-primary">
            {x.name}
            <span className="ml-1.5 text-text-tertiary">×{x.quantity}</span>
          </p>
          <span className="font-display text-sm font-semibold text-text-primary shrink-0">
            {yen(x.price * x.quantity)}
          </span>
        </li>
      ))}
    </ul>
  )
}
